import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { Users, MessageSquare, Heart, FileText, Calendar, CreditCard, TrendingUp, UserPlus } from "lucide-react";

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await apiClient.get('/admin/analytics');
        setAnalytics(res.data);
      } catch (err) {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  const statCards = analytics ? [
    { label: "Total Users", value: analytics.total_users, icon: Users, color: "bg-blue-500/10 text-blue-400" },
    { label: "New (7d)", value: analytics.new_users_7d, icon: UserPlus, color: "bg-emerald-500/10 text-emerald-400" },
    { label: "New (30d)", value: analytics.new_users_30d, icon: TrendingUp, color: "bg-violet-500/10 text-violet-400" },
    { label: "Mood Entries", value: analytics.total_mood_entries, icon: Heart, color: "bg-rose-500/10 text-rose-400" },
    { label: "Chat Messages", value: analytics.total_chat_messages, icon: MessageSquare, color: "bg-amber-500/10 text-amber-400" },
    { label: "Community Posts", value: analytics.total_community_posts, icon: FileText, color: "bg-cyan-500/10 text-cyan-400" },
    { label: "Bookings", value: analytics.total_bookings, icon: Calendar, color: "bg-indigo-500/10 text-indigo-400" },
    { label: "Paid Transactions", value: analytics.total_payments, icon: CreditCard, color: "bg-green-500/10 text-green-400" },
  ] : [];

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white mb-1">Analytics Overview</h1>
          <p className="text-white/40 text-sm">Platform metrics and user insights</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : analytics ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {statCards.map((stat) => (
                <div key={stat.label} className="bg-[#161821] border border-white/5 rounded-2xl p-5" data-testid={`stat-${stat.label.toLowerCase().replace(/[\s()]/g, '-')}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}>
                    <stat.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <p className="text-2xl font-semibold text-white">{stat.value}</p>
                  <p className="text-white/40 text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Subscription Tiers */}
              <div className="bg-[#161821] border border-white/5 rounded-2xl p-6">
                <h3 className="text-white font-medium mb-4">Users by Subscription</h3>
                <div className="space-y-3">
                  {Object.entries(analytics.tier_stats || {}).map(([tier, count]) => (
                    <div key={tier} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          tier === 'admin' ? 'bg-amber-400' :
                          tier === 'elite' ? 'bg-violet-400' :
                          tier === 'premium' ? 'bg-blue-400' :
                          tier === 'basic' ? 'bg-emerald-400' : 'bg-white/20'
                        }`}></div>
                        <span className="text-white/70 text-sm capitalize">{tier}</span>
                      </div>
                      <span className="text-white font-medium text-sm">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mood Distribution */}
              <div className="bg-[#161821] border border-white/5 rounded-2xl p-6">
                <h3 className="text-white font-medium mb-4">Mood Distribution</h3>
                {Object.keys(analytics.mood_distribution || {}).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(analytics.mood_distribution).map(([mood, count]) => {
                      const maxCount = Math.max(...Object.values(analytics.mood_distribution));
                      const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                      return (
                        <div key={mood}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-white/70 capitalize">{mood}</span>
                            <span className="text-white/50">{count}</span>
                          </div>
                          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-400/60 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-white/30 text-sm">No mood data yet</p>
                )}
              </div>

              {/* Recent Users */}
              <div className="bg-[#161821] border border-white/5 rounded-2xl p-6 lg:col-span-2">
                <h3 className="text-white font-medium mb-4">Recent Signups</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white/30 text-xs uppercase border-b border-white/5">
                        <th className="text-left pb-3 pr-4">Name</th>
                        <th className="text-left pb-3 pr-4">Email</th>
                        <th className="text-left pb-3 pr-4">Tier</th>
                        <th className="text-left pb-3 pr-4">Role</th>
                        <th className="text-left pb-3">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(analytics.recent_users || []).map((u, i) => (
                        <tr key={u.user_id || `user-${i}`} className="border-b border-white/5 last:border-0">
                          <td className="py-3 pr-4 text-white/80">{u.name}</td>
                          <td className="py-3 pr-4 text-white/50">{u.email}</td>
                          <td className="py-3 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              u.subscription_tier === 'elite' ? 'bg-violet-500/10 text-violet-400' :
                              u.subscription_tier === 'premium' ? 'bg-blue-500/10 text-blue-400' :
                              u.subscription_tier === 'admin' ? 'bg-amber-500/10 text-amber-400' :
                              'bg-white/5 text-white/40'
                            }`}>
                              {u.subscription_tier || "free"}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              u.role === 'admin' ? 'bg-amber-500/10 text-amber-400' : 'bg-white/5 text-white/40'
                            }`}>
                              {u.role || "user"}
                            </span>
                          </td>
                          <td className="py-3 text-white/40">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-white/30">Failed to load analytics</p>
        )}
      </div>
    </AdminLayout>
  );
}
